import { useEffect, useRef } from "react";

/**
 * One shared Escape handler for every Relay window and dialog
 * (track:track-spec-window-escape-every-relay-window-closes-with-esc-and-no-window-can-be-dragged-out-of-reach-60046).
 *
 * Open windows register on a stack; Esc closes only the most recently opened
 * one. Esc is ignored while an input method is composing text, and a focused
 * control that uses Esc itself (an open dropdown/menu, a search box with
 * text) handles it first — the window closes only if that control didn't.
 *
 * Callers pass the same handler as their × / Close button, so Esc does
 * exactly what × does and nothing more.
 */
type Entry = { onClose: () => void };
const stack: Entry[] = [];
let listening = false;

function controlHandlesEscape(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.getAttribute("aria-expanded") === "true") return true;
  if (target.closest('[role="menu"], [role="listbox"], [role="combobox"][aria-expanded="true"]')) return true;
  if (target instanceof HTMLInputElement && target.type === "search" && target.value !== "") return true;
  return false;
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== "Escape") return;
  if (e.defaultPrevented) return;
  if (e.isComposing || e.keyCode === 229) return;
  if (controlHandlesEscape(e.target)) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  e.preventDefault();
  top.onClose();
}

export function useEscapeToClose(open: boolean, onClose: () => void, options?: { enabled?: boolean }): void {
  const enabled = options?.enabled ?? true;
  const handlerRef = useRef(onClose);
  handlerRef.current = onClose;

  useEffect(() => {
    if (!open || !enabled) return;
    const entry: Entry = { onClose: () => handlerRef.current() };
    stack.push(entry);
    if (!listening) {
      window.addEventListener("keydown", onKeyDown);
      listening = true;
    }
    return () => {
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
      if (stack.length === 0 && listening) {
        window.removeEventListener("keydown", onKeyDown);
        listening = false;
      }
    };
  }, [open, enabled]);
}
